/*
 * File: APIErrorDialog.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles showing an error message from an API call
 */
import React, { useEffect } from "react";
import { createStyles, Theme, withStyles } from "@material-ui/core/styles";
import WarningIcon from "@material-ui/icons/Warning";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import { observer } from "mobx-react";
import UIDialog from "components/ui/UIDialog";
import { RouteComponentProps, withRouter } from "react-router";
import LoginStore from "services/login/Store";
import { WithStyles } from "@material-ui/styles";
import AppStore from "Store";
import { Instance } from "mobx-state-tree";

const styles = (theme: Theme) => createStyles({
  root: {
    padding: theme.spacing(2.5)
  },
  warningIcon: {
    color: "red",
    fontSize: theme.spacing(5),
    position: "relative",
    top: theme.spacing(0.5)
  },
  title: {
    color: "red",
    marginBottom: theme.spacing(2.5)
  }
});

interface Props extends WithStyles<typeof styles>, RouteComponentProps<Record<string, never>> {
  store: Instance<typeof AppStore>,
}

const APIErrorDialog = ({ store, classes }: Props) => {
  const { apiError } = store;
  const isAuthError = apiError?.response?.status === 401;

  useEffect(() => {
    if (isAuthError) {
      LoginStore.logout("API error");
    }
  }, [isAuthError]);

  if (!apiError) return <React.Fragment />;

  const closeDialog = () => {
    store.dismissError();
  };

  const content = () => (
    <div className={classes.root}>
      <Typography variant="h4" className={classes.title}>
        <WarningIcon className={classes.warningIcon} /> API Error
      </Typography>
      <Typography variant="h6">{apiError.message}</Typography>
    </div>
  );

  return (
    <UIDialog
      fullScreen
      open
      title="API Error"
      rightNav={() => <CloseIcon onClick={closeDialog} color="inherit" />}
      content={content}
    />
  );
};

export default withRouter(withStyles(styles)(observer(APIErrorDialog)));
